<template>
  <div>
    <div
      v-if="!sparkline"
      class="loading-indicator d-flex justify-center align-center flex-grow-1"
    >
      <v-progress-circular
        indeterminate
        color="primary"
        :size="110"
        :width="7"
      ></v-progress-circular>
      <span class="ml-3 text-h4">{{ $t("extras.loadingDot") }} </span>
    </div>
    <div
      v-if="sparklineDataIntraday?.filteredData || sparklineDataDaily"
      class="text-caption font-weight-black d-flex justify-center"
    >
      <v-chip-group
        v-model="selectedChart"
        mandatory
        @change="filterData(currentFilter)"
        show-arrows
      >
        <v-chip
          v-for="(chartName, index) in chartNames"
          color="#212121"
          text-color="#BDBDBD"
          active-class="primaryTransp primary--text"
          :key="index"
          :value="chartName"
          x-small
        >
          {{ chartName }}
        </v-chip>
      </v-chip-group>
    </div>
    <div
      v-if="sparkline"
      class="d-flex justify-center flex-column items-center"
      style="max-height: 500px; overflow-x: hidden; overflow-y: auto"
    >
      <v-chart
        :option="sparkline"
        class="chart"
        autoresize
        style="height: 18vh"
        ref="chart"
      ></v-chart>

      <div
        v-if="sparklineDataIntraday?.filteredData || sparklineDataDaily"
        class="my-3 d-flex justify-center align-center"
      >
        <v-btn-toggle
          v-model="currentFilter"
          mandatory
          @change="filterData(currentFilter)"
        >
          <v-tooltip v-if="dayOff" bottom>
            <template v-slot:activator="{ on, attrs }">
              <span v-on="on" v-bind="attrs">
                <v-btn
                  x-small
                  value="day"
                  :disabled="selectedChart !== 'USD/BRL' || dayOff"
                  >{{ $t("timestamp.oneDay") }}</v-btn
                >
              </span>
            </template>
            <span> {{ $t("extras.noDataAvailable") }}</span>
          </v-tooltip>
          <v-btn v-else x-small value="day">{{ $t("timestamp.oneDay") }}</v-btn>
          <v-btn x-small value="week">{{ $t("timestamp.oneWeek") }}</v-btn>
          <v-btn x-small value="month" :disabled="!sparklineDataDaily">{{
            $t("timestamp.oneMonth")
          }}</v-btn>
        </v-btn-toggle>
      </div>

      <div class="difference-values text-center text-caption">
        <p class="font-size-title">
          {{ differenceInValuesChart.firstValue.time }} -
          {{ differenceInValuesChart.lastValue.time }}
        </p>
        <p class="mb-2 font-size-title">
          {{ $t("marketMonitor.priceVariation") }}:
          <span
            class="font-size-title"
            :style="
              differenceInValuesChart.totalDifference.absolute.toFixed(4) < 0
                ? { color: '#FF6D00' }
                : { color: '#00c853' }
            "
          >
            {{ differenceInValuesChart.totalDifference.absolute.toFixed(4) }}
            ({{
              differenceInValuesChart.totalDifference.percentage.toFixed(2)
            }}%)
          </span>
        </p>
      </div>
    </div>
  </div>
</template>
<script>
import { LineChart } from "echarts/charts";
import { use } from "echarts/core";
import * as utils from '@/utils/utils';

import {
  TooltipComponent,
  TitleComponent,
  ToolboxComponent,
  GridComponent,
  DataZoomComponent,
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import {
  useFxMarketStore,
  useSettingsStore,
  useColorsStore,
} from "@/store/index";
import VChart from "vue-echarts";
import { mapState } from "pinia";
use([
  TooltipComponent,
  TitleComponent,
  ToolboxComponent,
  GridComponent,
  DataZoomComponent,
  LineChart,
  CanvasRenderer,
]);

export default {
  components: {
    VChart,
  },
  data() {
    return {
      filteredSparklineData: [],
      chartKey: null,
      firstPoint: { x: "", y: "" },
      secondPoint: { x: "", y: "" },
      currentFilter: "day",
      selectedChart: "USD/BRL",
      dayOff: false,
      lastTimeStap:null,
      chartInstance: null,
    };
  },
  mounted() {
    this.waitForSparkline();
    this.lastTimeStap = setInterval(()=>{
      this.filterData(this.currentFilter);
      this.renderChart()
    }, 6000)
  },
  beforeDestroy(){
    clearInterval(this.lastTimeStap)
  },
  watch: {
    currentFilter(newVal) {
      this.handleChartReloadInterval(newVal);
    },
    chartKey() {
      this.$nextTick(() => {
        const chart = this.$refs.chart;
        if (!chart) return;

        chart.chart.getZr().off("click");
        chart.chart.getZr().on("click", (e) => {
          const [x, y] = [e.offsetX, e.offsetY];

          const dataCoord = chart.convertFromPixel({ seriesIndex: 0 }, [x, y]);

          if (this.togglePositionData) {
            this.firstPoint = { x: dataCoord[0], y: dataCoord[1] };
          } else {
            this.secondPoint = { x: dataCoord[0], y: dataCoord[1] };
          }
          this.togglePositionData = !this.togglePositionData;
        });
      });
    },
  },
  computed: {
    ...mapState(useColorsStore, ["orangeColor", "aliveColor"]),
    ...mapState(useFxMarketStore, [
      "getFxMarketIntradayData",
      "getFxMarketEconomicCalendar",
      "getFxMarketNews",
      "getFxMarketDailyData",
      "getFxMarketIsLoading",
    ]),

    fxMarketDailyDataData() {
      if (!this.getFxMarketDailyData) return [];
      return this.getFxMarketDailyData.data;
    },
    fxMarketIntradayDataData() {
      if (!this.getFxMarketIntradayData) return [];
      return this.getFxMarketIntradayData.data;
    },
    sparklineDataDaily() {
      if (
        !this.fxMarketDailyDataData ||
        Object.keys(this.fxMarketDailyDataData).length === 0
      ) {
        return;
      }

      let selectedChart = this.selectedChart;
      if (this.selectedChart === undefined) {
        selectedChart = this.chartNames[0];
      }

      const selectedSparkline = this.fxMarketDailyDataData[selectedChart];

      const today = new Date();
      const oneMonthAgo = new Date();
      oneMonthAgo.setDate(today.getDate() - 31);

      const filteredDataMonth = [];

      for (const key in selectedSparkline) {
        const dateBrl = new Date(key).getTime() + 3 * 60 * 60 * 1000;

        if (
          dateBrl >= oneMonthAgo.getTime() &&
          dateBrl <= today.getTime() &&
          !this.isHoliday(new Date(dateBrl))
        ) {
          filteredDataMonth.push({
            timestamp: dateBrl,
            value: selectedSparkline[key]["1. open"],
          });
        }
      }

      filteredDataMonth.reverse();
      return {
        filteredDataMonth,
      };
    },
    sparklineDataIntraday() {
      if (
        !this.fxMarketIntradayDataData ||
        Object.keys(this.fxMarketIntradayDataData).length === 0 ||
        !this.chartNames
      ) {
        return {};
      }

      let selectedChart = this.selectedChart;
      if (this.selectedChart === undefined) {
        selectedChart = this.chartNames[0];
      }

      const selectedSparkline = this.fxMarketIntradayDataData[selectedChart];

      const today = new Date();
      const currentDay = today.getDay();
      const currentHour = today.getHours();
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(today.getDate() - 7);

      let startOfDayBRT, endOfDayBRT;

      if (currentHour < 9 || this.isWeekendOrHoliday) {
        const yesterday = new Date(today);
        if (currentDay === 0) {
          yesterday.setDate(today.getDate() - 2);
        } else {
          yesterday.setDate(today.getDate() - 1);
        }
        while (
          this.isHoliday(yesterday) ||
          yesterday.getDay() === 0 ||
          yesterday.getDay() === 6
        ) {
          yesterday.setDate(yesterday.getDate() - 1);
        }

        startOfDayBRT = new Date(yesterday.setHours(9, 0, 0, 0)).getTime();
        endOfDayBRT = new Date(yesterday.setHours(18, 30, 0, 0)).getTime();
      } else {
        startOfDayBRT = new Date(today.setHours(9, 0, 0, 0)).getTime();
        endOfDayBRT = new Date(today.setHours(18, 30, 0, 0)).getTime();
      }

      const filteredData = [];
      const filteredDataWeek = [];

      for (const key in selectedSparkline) {
        const dateUtc = new Date(key).getTime();
        const dateBrl = dateUtc - 3 * 60 * 60 * 1000;
        const dateBrlDate = new Date(dateBrl);
        const dayOfWeek = dateBrlDate.getDay();
        const hourOfDay = dateBrlDate.getHours();
        const minutesOfHour = dateBrlDate.getMinutes();

        if (dateBrl >= startOfDayBRT && dateBrl <= endOfDayBRT) {
          filteredData.push({
            timestamp: dateBrl,
            value: selectedSparkline[key]["4. close"],
          });
        }

        if (
          dateBrl >= oneWeekAgo.getTime() &&
          dateBrl <= today.getTime() &&
          dayOfWeek > 0 &&
          dayOfWeek < 6 &&
          !this.isHoliday(new Date(dateBrl)) &&
          ((hourOfDay >= 9 && hourOfDay < 18) ||
            (hourOfDay === 18 && minutesOfHour <= 30))
        ) {
          filteredDataWeek.push({
            timestamp: dateBrl,
            value: selectedSparkline[key]["4. close"],
          });
        }
      }

      filteredData.reverse();
      filteredDataWeek.reverse();

      return {
        filteredData,
        filteredDataWeek,
      };
    },
    minMax() {
      if (!this.filteredSparklineData) {
        return { min: 0, max: 0 };
      }
      const values = this.filteredSparklineData.map((point) =>
        parseFloat(point.value),
      );
      const min = Math.min(...values);
      const max = Math.max(...values);
      const interval = max - min;
      const adjustedMin = parseFloat(Math.max(min - interval * 1.1, 0)).toFixed(
        4,
      );
      const adjustedMax = parseFloat(max + interval * 1.1).toFixed(4);
      return { min: adjustedMin, max: adjustedMax };
    },
    xAxisInterval() {
      const dataLength = this.filteredSparklineData.length;
      const startTime = this.filteredSparklineData[0].timestamp;
      const endTime = this.filteredSparklineData[dataLength - 1].timestamp;
      const timeSpan = endTime - startTime;
      let minInterval;

      switch (this.currentFilter) {
        case "day":
          if (timeSpan <= 60 * 60 * 1000) {
            minInterval = 5 * 60 * 1000;
          } else if (timeSpan <= 4 * 60 * 60 * 1000) {
            minInterval = 10 * 60 * 1000;
          } else {
            minInterval = 30 * 60 * 1000;
          }
          return minInterval;
        // case 'week':
        //   minInterval = 24 * 60 * 60 * 1000
        //   return minInterval
        case "month":
          minInterval = 24 * 60 * 60 * 1000;
          return minInterval;
        default:
          return 0;
      }
    },
    chartNames() {
      if (!this.getfxMarketDailyData && !this.getFxMarketIntradayData)
        return [];
      return this.getfxMarketDailyData
        ? this.getfxMarketDailyData.tickers
        : this.getFxMarketIntradayData.tickers;
    },
    differenceInValuesChart() {
      if (
        !this.filteredSparklineData ||
        this.filteredSparklineData.length === 0
      )
        return;
      const options = JSON.parse(JSON.stringify(this.sparklinesOptions));

      const categories = this.filteredSparklineData.map((dataPoint) =>
        this.formatDate(dataPoint.timestamp, this.currentFilter),
      );
      const values = this.filteredSparklineData.map((dataPoint) =>
        parseFloat(dataPoint.value),
      );
      const firstValue = { time: categories[0], val: values[0] };
      const lastValue = {
        time: categories[categories.length - 1],
        val: values[values.length - 1],
      };
      const totalDifference = {
        absolute: lastValue.val - firstValue.val,
        percentage: ((lastValue.val - firstValue.val) / firstValue.val) * 100,
      };
      return {
        firstValue,
        lastValue,
        totalDifference,
      };
    },
    changeValue() {
      const data = this.filteredSparklineData;
      const length = data.length;
      const last = data[length - 1].value;
      const first = data[0].value;
      const result = last - first;
      return Number(result.toFixed(6));
    },
    sparkline() {
      if (
        !this.filteredSparklineData ||
        this.filteredSparklineData.length === 0
      )
        return;
      const options = JSON.parse(JSON.stringify(this.sparklinesOptions));

      const categories = this.filteredSparklineData.map((dataPoint) =>
        this.formatDate(dataPoint.timestamp, this.currentFilter),
      );
      const values = this.filteredSparklineData.map((dataPoint) =>
        parseFloat(dataPoint.value),
      );

      options.xAxis.data = categories;
      options.series[0].data = values;

      this.renderChartLastValue(options, values);

      if (this.currentFilter === "week") {
        let lastDay = "";
        // const lastIndex = categories.length - 1

        // options.xAxis.axisLabel.formatter = (value, index) => {
        options.xAxis.axisLabel.formatter = (value) => {
          const currentDay = value.split(" ")[0];

          // if (currentDay !== lastDay || index === lastIndex) {
          if (currentDay !== lastDay) {
            lastDay = currentDay;
            return currentDay;
          }

          return "";
        };
      }

      options.tooltip.formatter = (params) => {
        const dataPoint = params[0];
        const rawTimestamp =
          this.filteredSparklineData[dataPoint.dataIndex]?.timestamp;
        const formattedDate = this.formatDate(
          rawTimestamp,
          this.currentFilter,
          true,
          true,
        );
        const value = dataPoint.value;
        const v1 = this.filteredSparklineData[this.firstPoint.x]?.value;
        const v2 = this.filteredSparklineData[this.secondPoint.x]?.value;
        const differencePos = (v2 - v1).toFixed(3);
        const differencePercent = ((differencePos / v1) * 100).toFixed(3);
        //#FF6D00 -> -
        //#00c853 -> +
        return `
          ${formattedDate}
        <br>${dataPoint.marker} 
        ${dataPoint.seriesName}: 
        <b> ${value} `;
      };

      options.axisPointer = {
        label: {
          backgroundColor: "#777",
          formatter: (params) => categories[params.value],
        },
      };

      return options;
    },
    sparklinesOptions() {
      const scale = {
        fontScale: 10,
        gridMargin: 50,
        splitNumber: 5,
      };
      return {
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "cross",
            snap: true,
          },
        },
        grid: {
          top: 10,
          bottom: 25,
          left: 45,
          right: 60,
        },
        xAxis: {
          type: "category",
          boundaryGap: true,
          data: [],
          axisLabel: {
            fontSize: 10,
            showMinLabel: true,
            showMaxLabel: true,
            interval: 4,
          },
          axisLine: { show: true },
          axisTick: {
            show: true,
            alignWithLabel: true,
          },
        },
        yAxis: [
          {
            type: "value",
            ...this.minMax,
            splitNumber: 4,
            splitLine: { show: false },
            opacity:0,
            position: "left",
            axisLabel: {
              fontSize: 10,
            },
          },
          {
            type: "value",
            ...this.minMax,
            splitNumber: 4,
            splitLine: { show: false },
            position: "right",
            axisLabel: {
              fontSize: 10,
              formatter:(value)=>Number(value).toFixed(4)
            },
          },
        ],
        series: [
          {
            name: this.$t("currencyChart.seriesData"),
            type: "line",
            symbol: "circle",
            sampling: "lttb",
            yAxisIndex:1,
            itemStyle: {
              color: this.changeValue > 0 ? this.aliveColor : this.orangeColor,
            },
            areaStyle: {
              color: {
                type: "linear",
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  {
                    offset: 0,
                    color:
                      this.changeValue > 0 ? this.aliveColor : this.orangeColor,
                  },
                  {
                    offset: 1,
                    color: "rgba(0, 0, 0, 0)",
                  },
                ],
              },
            },
            data: [],
          },
        ],
      };
    },
  },
  methods: {
    renderChart(){
      if(!this.chartInstance || !this.filteredSparklineData?.length) return
      const categories = this.filteredSparklineData.map(p=>this.formatDate(p.timestamp, this.currentFilter))
      const values = this.filteredSparklineData.map(p=>Number(p.value));

      const options = {
          xAxis: { data: categories },
          series: [
            {
              id: 'main',
              type: 'line',
              data: values,
            }
          ]
        };
        this.renderChartLastValue(options, values);
        this.chartInstance.appendData({
          seriesIndex: 0,
          data: [[last.timestamp, newValue]]
        })
    },
    renderChartLastValue(options, values) {
      let dataLength = options.series[0].data
      let finalIndex = dataLength.length - 1
      options.series[0].data = values.map((v,idx)=>{
        if (idx === finalIndex)
        {
          return {
            value:v,
            symbol:"circle",
            symbolSize: 7
          }
        }
        return {
            value:v,
            symbol:"none",
          }
      })
      const lastVal = values[values.length-1]; 

      options.series[0].markLine = {
      silent: true,
      symbol: "none",
        //#FF6D00 -> -
        //#00c853 -> +
      data: [{
        yAxis: lastVal.toFixed(4),
        label: {
          show: true,
          distance: 5,
          formatter: lastVal.toFixed(4),
          borderWidth: 1,
          borderColor:  this.differenceInValuesChart.totalDifference.absolute.toFixed(4) < 0 ? '#FF6D00' : '#00c853',
          backgroundColor:  'none',
          color: this.differenceInValuesChart.totalDifference.absolute.toFixed(4) < 0 ? '#FF6D00' : '#00c853',
          padding: [2, 6],
          borderRadius: 4
        },
      }]
    };

    const lastIndex = values.length - 1;
    const lastCategory = options.xAxis.data[lastIndex];
    const lastValue = values[lastIndex];

    options.series[1] = {
        type: "effectScatter",
        coordinateSystem: "cartesian2d",
        z: 10,
        data: [[lastCategory, lastValue]],
        symbolSize: 5,
        rippleEffect: {
          scale: 7,
          brushType: "stroke",
        },
        itemStyle: {
          color:
            this.differenceInValuesChart.totalDifference.absolute < 0
              ? "#FF6D00"
              : "#00c853",
        },
        showEffectOn: "render",
        animationDuration: 0,
        animationDurationUpdate: 0,
      };
    },
    filterData(period) {
      const monthData = this.sparklineDataDaily;
      const data = this.sparklineDataIntraday;

      this.currentFilter = period;
      if (period === "day") {
        if (data["filteredData"]?.length === 0) {
          this.dayOff = true;
          this.currentFilter = "week";
          this.filteredSparklineData = data["filteredDataWeek"];
        } else {
          this.filteredSparklineData = data["filteredData"];
          this.dayOff = false;
        }
      } else if (period === "month") {
        this.filteredSparklineData = monthData["filteredDataMonth"];
      } else {
        this.filteredSparklineData = data["filteredDataWeek"];
      }
    },
    isHoliday(date) {
      if (!this.holidaysList || this.holidaysList.length === 0) return false;
      const formattedDate = date.toISOString().split("T")[0];
      return this.holidaysList.some(
        (holiday) => holiday.date === formattedDate,
      );
    },
    handleChartReloadInterval(filter) {
      if (this.chartReloadInterval) {
        clearInterval(this.chartReloadInterval);
        this.chartReloadInterval = null;
      }

      if (filter === "day" || filter === "week") {
        this.chartReloadInterval = setInterval(() => {
          this.chartKey += 1;
        }, 25000);
      }
    },
    waitForSparkline(attempts = 0, maxAttempts = 20) {
      if(attempts === 0) {
        this.filterData(this.currentFilter);
      }

      if (this.sparkline) {
        this.renderChart()
      } else if (attempts < maxAttempts) {
        setTimeout(
          () => this.waitForSparkline(attempts + 1, maxAttempts),
          3000,
        );
      }
    },
    formatDate(value, filter, forTooltip = false) {
      const { year, month, day, hour, minute } = utils.getDateParts(
        value,
        true,
      );

      const labelDate = [day, month];
      const labelHour = [hour, minute];
      const labelYear = year;

      if (forTooltip) {
        if (filter === "day" || filter === "week") {
          return `
          ${labelYear}-${labelDate.join("-")} <br>
          ${labelHour.join(":")}
          `;
        }
        if (filter === "month") {
          return `${labelYear}-${labelDate.join("-")}`;
        }
      }

      if (filter === "day") {
        return labelHour.join(":");
      } else {
        return labelDate.join("/");
      }
    },
    fetchHolidaysList() {
      this.getHolidaysList()
        .then((response) => {
          this.holidaysList.push(...response.holidays);
        })
        .catch((error) => {
          console.error(e.response?.message, e.response || error);
        });
    },
  },
};
</script>
<style scoped>
.font-size-title {
  font-size: 0.7rem !important;
  line-height: 0.45rem;
}
</style>
